import { useState, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Copy, Save, FileText, Layout, Palette, AlignLeft, Hash, List, Eye, Plus, Edit, RefreshCw, MessageSquare, Upload } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import type { FormatAnalysisResult } from '@creanizer/types'
import { useScenarioStore } from '../../../../stores/scenarioStore'
import { useGenerator } from '../../../../hooks/useGenerator'
import { readMultipleFiles, FILE_ACCEPT } from '../../utils/fileReader'
// Toast notifications removed

interface FormatAnalysisResultProps {
  result: FormatAnalysisResult
  originalInput: {
    formatName?: string
    formatDescription?: string
    sourceType?: string
    sourceText?: string
  }
  onResultUpdate?: (result: FormatAnalysisResult) => void
}

export function FormatAnalysisResult({ result, originalInput, onResultUpdate }: FormatAnalysisResultProps) {
  const { addFormat } = useScenarioStore()
  const [additionalSource, setAdditionalSource] = useState('')
  const [correctionInstruction, setCorrectionInstruction] = useState('')
  const [isRefining, setIsRefining] = useState(false)
  const [activeTab, setActiveTab] = useState('view')
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([])
  const [additionalSourceType, setAdditionalSourceType] = useState<'scenario' | 'template' | 'outline' | 'mixed'>('scenario')
  const [currentResult, setCurrentResult] = useState(result)
  const [isSaved, setIsSaved] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const generator = useGenerator('scenario')
  
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    console.log(`${label}をコピーしました`)
  }
  
  const handleSave = () => {
    addFormat({
      name: originalInput.formatName || currentResult.formatInfo?.name || '無題のフォーマット',
      input: {
        mode: 'analyze_format',
        formatAnalysisSettings: {
          sourceType: (originalInput.sourceType as 'scenario' | 'template' | 'outline' | 'mixed') || 'scenario',
          sourceText: originalInput.sourceText || '',
          formatName: originalInput.formatName,
          formatDescription: originalInput.formatDescription,
          extractStructure: true,
          extractStyle: true,
          extractPatterns: true
        }
      },
      result: currentResult,
      category: currentResult.formatInfo?.style || 'structured',
      tags: []
    })
    setIsSaved(true)
    console.log('フォーマットを保存しました')
  }
  
  const formatFormatData = () => {
    return JSON.stringify(currentResult, null, 2)
  }
  
  // ファイルアップロード処理
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    
    setIsProcessingFiles(true)
    setUploadedFiles([])
    
    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)
      
      if (errors.length > 0) {
        console.error('ファイル読み込みエラー:', errors)
      }
      
      if (combinedText) {
        setAdditionalSource(prev => {
          const newText = prev ? `${prev}\n\n${combinedText}` : combinedText
          return newText
        })
        setUploadedFiles(fileInfos.map(info => info.fileName))
      }
    } catch (error) {
      console.error('ファイル処理エラー:', error)
    } finally {
      setIsProcessingFiles(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }
  
  // 追加解析処理
  const handleAdditionalAnalysis = async () => {
    if (!additionalSource.trim()) return
    
    setIsRefining(true)
    try {
      const refinedInput = {
        mode: 'analyze_format' as const,
        formatAnalysisSettings: {
          sourceType: additionalSourceType,
          sourceText: `【既存の解析結果】\n${JSON.stringify(currentResult, null, 2)}\n\n【追加ソース（タイプ: ${additionalSourceType}）】\n${additionalSource}`,
          formatName: originalInput.formatName,
          formatDescription: originalInput.formatDescription,
          extractStructure: true,
          extractStyle: true,
          extractPatterns: true
        }
      }
      
      const response = await generator.mutateAsync(refinedInput)
      const newResult = response.result || response
      
      if (newResult) {
        setCurrentResult(newResult as FormatAnalysisResult)
        if (onResultUpdate) {
          onResultUpdate(newResult as FormatAnalysisResult)
        }
        setIsSaved(false) // 新しい結果があるので保存状態をリセット
        console.log('追加解析を実行し、結果を更新しました')
        setAdditionalSource('')
        setUploadedFiles([])
        setActiveTab('view') // 結果表示タブに切り替え
      }
    } catch (error) {
      console.error('追加解析エラー:', error)
    } finally {
      setIsRefining(false)
    }
  }
  
  // 修正指示処理
  const handleCorrection = async () => {
    if (!correctionInstruction.trim()) return
    
    setIsRefining(true)
    try {
      const correctionInput = {
        mode: 'analyze_format' as const,
        formatAnalysisSettings: {
          sourceType: 'mixed' as const,
          sourceText: `【現在の解析結果】\n${JSON.stringify(currentResult, null, 2)}\n\n【修正指示】\n${correctionInstruction}\n\n上記の修正指示に従って、解析結果を修正してください。`,
          formatName: originalInput.formatName,
          formatDescription: originalInput.formatDescription,
          extractStructure: true,
          extractStyle: true,
          extractPatterns: true
        }
      }
      
      const response = await generator.mutateAsync(correctionInput)
      const newResult = response.result || response
      
      if (newResult) {
        setCurrentResult(newResult as FormatAnalysisResult)
        if (onResultUpdate) {
          onResultUpdate(newResult as FormatAnalysisResult)
        }
        setIsSaved(false) // 新しい結果があるので保存状態をリセット
        console.log('修正指示を実行し、結果を更新しました')
        setCorrectionInstruction('')
        setActiveTab('view') // 結果表示タブに切り替え
      }
    } catch (error) {
      console.error('修正処理エラー:', error)
    } finally {
      setIsRefining(false)
    }
  }
  
  return (
    <div className="space-y-6">
      {/* タブナビゲーション */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="view">
            <FileText className="w-4 h-4 mr-2" />
            結果表示
          </TabsTrigger>
          <TabsTrigger value="refine">
            <RefreshCw className="w-4 h-4 mr-2" />
            追加解析
          </TabsTrigger>
          <TabsTrigger value="correct">
            <Edit className="w-4 h-4 mr-2" />
            修正指示
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="view" className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {currentResult.formatInfo?.name || originalInput.formatName || '解析済みフォーマット'}
              </CardTitle>
              {(result.formatInfo?.description || originalInput.formatDescription) && (
                <CardDescription>
                  {currentResult.formatInfo?.description || originalInput.formatDescription}
                </CardDescription>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(formatFormatData(), 'フォーマットデータ')}
              >
                <Copy className="w-4 h-4 mr-2" />
                コピー
              </Button>
              <Button
                size="sm"
                variant={isSaved ? 'secondary' : 'default'}
                onClick={handleSave}
                disabled={isSaved}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaved ? '保存済み' : '保存'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              ソース: {getSourceTypeLabel(originalInput.sourceType)}
            </Badge>
            {currentResult.formatType && (
              <Badge>
                タイプ: {currentResult.formatType}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
      
      {currentResult.structure && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layout className="w-5 h-5" />
              構造
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.structure.sections && result.structure.sections.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">セクション構成</h4>
                <div className="space-y-2">
                  {currentResult.structure.sections.map((section, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Badge variant="outline" className="mt-0.5">
                        {index + 1}
                      </Badge>
                      <div className="flex-1">
                        <div className="font-medium">{section.name}</div>
                        {section.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {section.description}
                          </p>
                        )}
                        {section.required && (
                          <Badge variant="secondary" className="mt-1">
                            必須
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {currentResult.structure.flow && (
              <div>
                <h4 className="font-semibold mb-2">フロー</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {currentResult.structure.flow}
                </p>
              </div>
            )}
            
            {currentResult.structure.pacing && (
              <div>
                <h4 className="font-semibold mb-2">ペーシング</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.structure.pacing}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.writingStyle && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              文体
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.writingStyle.tone && (
              <div>
                <h4 className="font-semibold mb-2">トーン</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.writingStyle.tone}
                </p>
              </div>
            )}
            
            {currentResult.writingStyle.perspective && (
              <div>
                <h4 className="font-semibold mb-2">視点</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.writingStyle.perspective}
                </p>
              </div>
            )}
            
            {currentResult.writingStyle.voice && (
              <div>
                <h4 className="font-semibold mb-2">語り口</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.writingStyle.voice}
                </p>
              </div>
            )}
            
            {currentResult.writingStyle.tense && (
              <div>
                <h4 className="font-semibold mb-2">時制</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.writingStyle.tense}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.patterns && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5" />
              パターン
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.patterns.expressions && result.patterns.expressions.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">定型表現</h4>
                <div className="space-y-2">
                  {currentResult.patterns.expressions.map((expr, index) => (
                    <div key={index} className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <code className="text-sm">{expr}</code>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {currentResult.patterns.transitions && result.patterns.transitions.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">遷移パターン</h4>
                <div className="space-y-2">
                  {currentResult.patterns.transitions.map((trans, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Badge variant="outline">{index + 1}</Badge>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {trans}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {currentResult.patterns.resolutions && result.patterns.resolutions.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">解決パターン</h4>
                <div className="space-y-2">
                  {currentResult.patterns.resolutions.map((res, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-gray-500">•</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {res}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.elements?.essential && result.elements.essential.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <List className="w-5 h-5" />
              必須要素
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentResult.elements.essential.map((element, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-sm">{element}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {currentResult.elements?.optional && result.elements.optional.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              オプション要素
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentResult.elements.optional.map((element, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {element}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {currentResult.writingStyle?.examples && result.writingStyle.examples.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>解析サンプル</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentResult.writingStyle.examples.map((example, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono">
                    {example}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>
        
        {/* 追加解析タブ */}
        <TabsContent value="refine" className="space-y-6">
          <Alert>
            <Plus className="h-4 w-4" />
            <AlertDescription>
              追加のシナリオサンプルを入力して、フォーマットパターンをより詳細に解析できます。
              同じフォーマットの別のシナリオや、追加のセクションを入力してください。
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>追加ソース入力</CardTitle>
              <CardDescription>
                既存の解析結果に統合したい追加のシナリオサンプルを入力してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="source-type">ソースの種類</Label>
                <Select value={additionalSourceType} onValueChange={(value: any) => setAdditionalSourceType(value)}>
                  <SelectTrigger id="source-type" className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scenario">シナリオ本文</SelectItem>
                    <SelectItem value="template">テンプレート</SelectItem>
                    <SelectItem value="outline">アウトライン</SelectItem>
                    <SelectItem value="mixed">混合（複数種類）</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  追加するソースの種類を選択してください。精度向上のため適切なタイプを選ぶことを推奨します。
                </p>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="additional-source">追加テキスト</Label>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={FILE_ACCEPT}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isProcessingFiles}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {isProcessingFiles ? 'ファイル処理中...' : 'ファイルから読込'}
                    </Button>
                    {additionalSource && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setAdditionalSource('')
                          setUploadedFiles([])
                        }}
                      >
                        クリア
                      </Button>
                    )}
                  </div>
                </div>
                
                {uploadedFiles.length > 0 && (
                  <Alert className="mb-2">
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      読み込んだファイル: {uploadedFiles.join(', ')}
                    </AlertDescription>
                  </Alert>
                )}
                
                <Textarea
                  id="additional-source"
                  value={additionalSource}
                  onChange={(e) => setAdditionalSource(e.target.value)}
                  placeholder="例：同じフォーマットの別のシナリオ、追加のセクション、バリエーションパターンなど（ファイルアップロードまたは直接入力）"
                  rows={10}
                  className="mt-2 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {additionalSource.length.toLocaleString()}文字
                </p>
              </div>
              
              <Button
                onClick={handleAdditionalAnalysis}
                disabled={!additionalSource.trim() || isRefining}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {isRefining ? '解析中...' : '追加解析を実行'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* 修正指示タブ */}
        <TabsContent value="correct" className="space-y-6">
          <Alert>
            <MessageSquare className="h-4 w-4" />
            <AlertDescription>
              フォーマット解析結果の間違いや不足を指摘して、修正指示を出すことができます。
              具体的な修正内容を日本語で記述してください。
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>修正指示</CardTitle>
              <CardDescription>
                現在の解析結果に対する修正指示を入力してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="correction-instruction">修正内容</Label>
                <Textarea
                  id="correction-instruction"
                  value={correctionInstruction}
                  onChange={(e) => setCorrectionInstruction(e.target.value)}
                  placeholder="例：
・導入部分は「フック」ではなく「オープニング」という名称を使用
・戦闘シーンは必須ではなくオプションです
・NPCの記述フォーマットが違います。正しくは...
・各セクションの文字数の目安が間違っています"
                  rows={10}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {correctionInstruction.length.toLocaleString()}文字
                </p>
              </div>
              
              <Button
                onClick={handleCorrection}
                disabled={!correctionInstruction.trim() || isRefining}
                className="w-full"
              >
                <Edit className="w-4 h-4 mr-2" />
                {isRefining ? '修正中...' : '修正を適用'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function getSourceTypeLabel(sourceType?: string): string {
  if (!sourceType) return '不明'
  const labels: Record<string, string> = {
    scenario: '完成シナリオ',
    template: 'テンプレート',
    outline: 'アウトライン',
    mixed: '混合'
  }
  return labels[sourceType] || sourceType
}